import { useEffect, memo, useState } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { isEqual } from "lodash";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { NONE } from "../../consts/strings.js";
import { ROBOT_DRIVER_TYPE } from "../../consts/driverTypes.js";
import { ROBOT_VEHICLE_TYPE } from "../../consts/robot.js";
import { palette } from "../../theme/palette.js";

import { getDriverDayFormData } from "../../utils/drivers.js";

import { depotDriverValuesSelector } from "../../reducers/depot/selectors.js";

import { View } from "../View.jsx";
import { WorkScheduleDayTextField } from "./InputFields/WorkScheduleDayTextField.jsx";
import { WorkScheduleDayCheckbox } from "./InputFields/WorkScheduleDayCheckbox.jsx";
import { WorkScheduleDayAutocomplete } from "./InputFields/WorkScheduleDayAutocomplete.jsx";

const useOwnStyles = makeStyles({
  textField: {
    width: "100%",
  },
  dropDownErrorHelper: {
    marginRight: "5px",
    marginBottom: "-3%",
  },
  disabled: {
    pointerEvents: "none",
    "& input": {
      color: palette.typography.black.disabled,
    },
  },
  hidden: {
    display: "none",
  },
});

export const WorkScheduleDayForm = ({
  styles = {},
  onChange = async () => {},
  onValidation = () => {},
  sections = [],
  vehicles = {},
  routes = [],
  drivers = {},
  driverEdit = {},
  viewOnly = false,
  disabledControls = {},
  hiddenControls = {},
  hideCheckboxInfoIcon = false,
}) => {
  const ownStyles = useOwnStyles();
  const depotDriverValues = useSelector(depotDriverValuesSelector);
  const [isProcessing, setIsProcessing] = useState(false);
  const onControlChange = async ({ target: { name, value } }) => {
    // This prevents users from editing driver more then once before driver is locked
    // what could potentially lead to 2nd driver lock request being send
    // for the same driver before the first one has been settled
    if (isProcessing) return;
    setIsProcessing(true);
    await onChange({ name, value });
    setIsProcessing(false);
  };

  let errors, hasErrors, odfCode, selectableOdfs, selectableDrivers;
  ({
    driverEdit,
    errors,
    hasErrors,
    odfCode,
    selectableOdfs,
    selectableDrivers,
  } = getDriverDayFormData({
    driverEdit,
    vehicles,
    routes,
    drivers,
    depotDriverValues,
  }));

  useEffect(() => {
    onValidation(hasErrors);
  }, [hasErrors, onValidation]);

  const renderOdfDropDown = () => (
    <View
      className={
        !hiddenControls["plannedOdf"]
          ? clsx(styles.commonControlContainer, styles.odfDropDownContainer)
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayAutocomplete
        className={styles.odfDropDown}
        label={"ODF"}
        name={"plannedOdf"}
        error={errors["plannedOdf"]}
        helperText={errors["plannedOdf"]}
        drivers={selectableOdfs}
        selectedDriver={drivers[odfCode]}
        onChange={onControlChange}
        disabled={viewOnly || !!disabledControls["plannedOdf"]}
      />
    </View>
  );

  const renderDriverDropDown = () => (
    <View
      className={
        !hiddenControls["driverCode"]
          ? clsx(styles.commonControlContainer, styles.driverDropDownContainer)
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayAutocomplete
        className={styles.driverDropDown}
        label={"Driver"}
        name={"driverCode"}
        error={errors["driverCode"]}
        helperText={errors["driverCode"]}
        drivers={selectableDrivers}
        selectedDriver={drivers[driverEdit.driverCode]}
        onChange={onControlChange}
        disabled={viewOnly || !!disabledControls["driverCode"]}
      />
    </View>
  );

  const renderRouteTextField = () => (
    <View
      className={
        !hiddenControls["routeCode"]
          ? clsx(styles.commonControlContainer, styles.routeCodeContainer)
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        mask={"999"}
        driverEdit={
          viewOnly && !driverEdit.routeCode ? { routeCode: "___" } : driverEdit
        }
        name={"routeCode"}
        label={"Route"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderDutyStartTimeTextField = () => (
    <View
      className={
        !hiddenControls["dutyStartTime"]
          ? clsx(styles.commonControlContainer, styles.dutyStartTimeContainer)
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        driverEdit={driverEdit}
        name={"dutyStartTime"}
        label={"Duty Start"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderDutyEndTimeTextField = () => (
    <View
      className={
        !hiddenControls["dutyEndTime"]
          ? clsx(styles.commonControlContainer, styles.dutyEndTimeContainer)
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        driverEdit={driverEdit}
        name={"dutyEndTime"}
        label={"Duty End"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderTotalDutyTimeTextField = () => (
    <View
      className={
        !hiddenControls["totalDutyTime"]
          ? clsx(styles.commonControlContainer, styles.totalDutyTimeContainer)
          : ownStyles.hidden
      }
    >
      <TextField
        className={clsx(ownStyles.textField, ownStyles.disabled)}
        variant={"outlined"}
        label={"Total Duty"}
        name={"totalDutyTime"}
        value={driverEdit.totalDutyTime || ""}
        error={!!errors["totalDutyTime"]}
        helperText={errors["totalDutyTime"]}
        FormHelperTextProps={{
          style: { marginLeft: "0px", marginBottom: "-0.5rem" },
        }}
      />
    </View>
  );

  const renderPlannedDepartTimeTextField = () => (
    <View
      className={
        !hiddenControls["plannedDepartTime"]
          ? clsx(
              styles.commonControlContainer,
              styles.plannedDepartTimeContainer
            )
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        driverEdit={driverEdit}
        name={"plannedDepartTime"}
        label={"Depart"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderPlannedReturnTimeContainerTextField = () => (
    <View
      className={
        !hiddenControls["plannedReturnTime"]
          ? clsx(
              styles.commonControlContainer,
              styles.plannedReturnTimeContainer
            )
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        driverEdit={driverEdit}
        name={"plannedReturnTime"}
        label={"Return"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderAmTimeInDepotTextField = () => (
    <View
      className={
        !hiddenControls["amTimeInDepot"]
          ? clsx(styles.commonControlContainer, styles.amTimeInDepotContainer)
          : ownStyles.hidden
      }
    >
      <TextField
        className={clsx(ownStyles.textField, ownStyles.disabled)}
        variant={"outlined"}
        label={"AM TID"}
        name={"amTimeInDepot"}
        value={driverEdit.amTimeInDepot || ""}
        FormHelperTextProps={{
          style: { marginLeft: "0px", marginBottom: "-0.5rem" },
        }}
        error={!!errors["amTimeInDepot"]}
        helperText={errors["amTimeInDepot"]}
      />
    </View>
  );

  const renderPmTimeInDepotTextField = () => (
    <View
      className={
        !hiddenControls["pmTimeInDepot"]
          ? clsx(styles.commonControlContainer, styles.pmTimeInDepotContainer)
          : ownStyles.hidden
      }
    >
      <TextField
        className={clsx(ownStyles.textField, ownStyles.disabled)}
        variant={"outlined"}
        label={"PM TID"}
        name={"pmTimeInDepot"}
        value={driverEdit.pmTimeInDepot || ""}
        FormHelperTextProps={{
          style: { marginLeft: "0px", marginBottom: "-0.5rem" },
        }}
        error={!!errors["pmTimeInDepot"]}
        helperText={errors["pmTimeInDepot"]}
      />
    </View>
  );

  const renderDeliveryStopTimeTextField = () => (
    <View
      className={
        !hiddenControls["deliveryStopTime"]
          ? clsx(
              styles.commonControlContainer,
              styles.deliveryStopTimeContainer
            )
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        driverEdit={driverEdit}
        name={"deliveryStopTime"}
        label={"Delivery Stop"}
        helperText={"MM:SS"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderCollectionStopTimeTextField = () => (
    <View
      className={
        !hiddenControls["collectionStopTime"]
          ? clsx(
              styles.commonControlContainer,
              styles.collectionStopTimeContainer
            )
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayTextField
        driverEdit={driverEdit}
        label={"Collection Stop"}
        name={"collectionStopTime"}
        helperText={"MM:SS"}
        viewOnly={viewOnly}
        disabledControls={disabledControls}
        errors={errors}
        onChange={onControlChange}
      />
    </View>
  );

  const renderVehicleDropDown = () => (
    <View
      className={
        !hiddenControls["vehicleType"]
          ? clsx(styles.commonControlContainer, styles.vehicleDropDownContainer)
          : ownStyles.hidden
      }
    >
      <FormControl className={styles.vehicleDropDown} variant={"outlined"}>
        <InputLabel id="vehicle">Vehicle</InputLabel>
        <Select
          label={"Vehicle"}
          labelId={"vehicle"}
          name={"vehicleType"}
          disabled={viewOnly || !!disabledControls["vehicleType"]}
          value={driverEdit.vehicleType || ""}
        >
          {driverEdit.driverType === ROBOT_DRIVER_TYPE
            ? [
                <MenuItem key={"Robot"} value={ROBOT_VEHICLE_TYPE}>
                  Robot
                </MenuItem>,
              ]
            : [
                <MenuItem key={NONE} value={NONE}>
                  {NONE}
                </MenuItem>,
                ...Object.keys(vehicles).map((vehicleType, index) => (
                  <MenuItem key={index} value={vehicleType}>
                    {vehicles[vehicleType].description}
                  </MenuItem>
                )),
              ]}
        </Select>
      </FormControl>
    </View>
  );

  const renderRobotTypeDropDown = () => (
    <View
      className={
        !hiddenControls["robotTypeCode"]
          ? clsx(
              styles.commonControlContainer,
              styles.robotTypeCodeDropDownContainer
            )
          : ownStyles.hidden
      }
    >
      <FormControl
        className={styles.robotTypeCodeDropDown}
        variant={"outlined"}
      >
        <InputLabel id="robotTypeCode" error={!!errors["robotTypeCode"]}>
          Robot Type
        </InputLabel>
        <Select
          label={"Robot Type"}
          labelId={"robotTypeCode"}
          name={"robotTypeCode"}
          value={driverEdit.robotTypeCode || NONE}
          disabled={viewOnly || !!disabledControls["robotTypeCode"]}
          error={!!errors["robotTypeCode"]}
          onChange={onControlChange}
        >
          {[
            <MenuItem key={NONE} value={NONE}>
              {NONE}
            </MenuItem>,
            <MenuItem
              key={driverEdit.robotTypeCode}
              value={driverEdit.robotTypeCode}
            >
              {driverEdit.robotTypeDescription}
            </MenuItem>,
          ]}
        </Select>
        <FormHelperText
          className={clsx(
            !!errors["robotTypeCode"] && ownStyles.dropDownErrorHelper
          )}
          error={!!errors["robotTypeCode"]}
        >
          {errors["robotTypeCode"] || ""}
        </FormHelperText>
      </FormControl>
    </View>
  );

  const renderSectionDropDown = () => (
    <View
      className={
        !hiddenControls["sectionCode"]
          ? clsx(styles.commonControlContainer, styles.sectionDropDownContainer)
          : ownStyles.hidden
      }
    >
      <FormControl className={styles.sectionDropDown} variant={"outlined"}>
        <InputLabel id="section" error={!!errors["sectionCode"]}>
          Section
        </InputLabel>
        <Select
          label={"Section"}
          labelId={"section"}
          name={"sectionCode"}
          value={driverEdit.sectionCode || 0}
          disabled={viewOnly || !!disabledControls["sectionCode"]}
          error={!!errors["sectionCode"]}
          onChange={onControlChange}
        >
          {[
            <MenuItem key={NONE} value={0}>
              {NONE}
            </MenuItem>,
            ...sections.map(({ sectionCode, sectionName }) => {
              return (
                <MenuItem key={sectionCode} value={sectionCode}>
                  {sectionName}
                </MenuItem>
              );
            }),
          ]}
        </Select>
        <FormHelperText
          className={clsx(
            !!errors["sectionCode"] && ownStyles.dropDownErrorHelper
          )}
          error={!!errors["sectionCode"]}
        >
          {errors["sectionCode"] || ""}
        </FormHelperText>
      </FormControl>
    </View>
  );

  const renderLeaveFromCheckbox = () => (
    <View
      className={
        !hiddenControls["startAtSatellite"]
          ? clsx(
              styles.commonControlContainer,
              styles.leaveFromCheckboxContainer
            )
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayCheckbox
        driverEdit={driverEdit}
        name={"startAtSatellite"}
        hideInfoIcon={hideCheckboxInfoIcon}
        disabled={viewOnly || !!disabledControls["startAtSatellite"]}
        message={"Leave From Satellite Depot"}
        onChange={onControlChange}
      />
    </View>
  );

  const renderReturnToCheckbox = () => (
    <View
      className={
        !hiddenControls["endAtSatellite"]
          ? clsx(
              styles.commonControlContainer,
              styles.returnToCheckboxContainer
            )
          : ownStyles.hidden
      }
    >
      <WorkScheduleDayCheckbox
        driverEdit={driverEdit}
        name={"endAtSatellite"}
        hideInfoIcon={hideCheckboxInfoIcon}
        disabled={viewOnly || !!disabledControls["endAtSatellite"]}
        message={"Return To Satellite Depot"}
        onChange={onControlChange}
      />
    </View>
  );

  return (
    <View className={styles.formContainer}>
      {renderOdfDropDown()}
      {renderDriverDropDown()}
      {renderRouteTextField()}
      {renderDutyStartTimeTextField()}
      {renderDutyEndTimeTextField()}
      {renderTotalDutyTimeTextField()}
      {renderPlannedDepartTimeTextField()}
      {renderPlannedReturnTimeContainerTextField()}
      {renderAmTimeInDepotTextField()}
      {renderPmTimeInDepotTextField()}
      {renderDeliveryStopTimeTextField()}
      {renderCollectionStopTimeTextField()}
      {renderVehicleDropDown()}
      {renderRobotTypeDropDown()}
      {renderSectionDropDown()}
      {renderLeaveFromCheckbox()}
      {renderReturnToCheckbox()}
    </View>
  );
};

export const WorkScheduleDayFormMemo = memo(
  WorkScheduleDayForm,
  (
    {
      styles: prevStyles,
      onChange: prevOnChange,
      onValidation: prevOnValidation,
      sections: prevSections,
      vehicles: prevVehicles,
      routes: prevRoutes,
      drivers: prevDrivers,
      driverEdit: prevDriverEdit,
      disabledControls: prevDisabledControls,
      hiddenControls: prevHiddenControls,
      hideCheckboxInfoIcon: prevHideCheckboxInfoIcon,
      viewOnly: prevViewOnly,
    },
    {
      styles: nextStyles,
      onChange: nextOnChange,
      onValidation: nextOnValidation,
      sections: nextSections,
      vehicles: nextVehicles,
      routes: nextRoutes,
      drivers: nextDrivers,
      driverEdit: nextDriverEdit,
      disabledControls: nextDisabledControls,
      hiddenControls: nextHiddenControls,
      hideCheckboxInfoIcon: nextHideCheckboxInfoIcon,
      viewOnly: nextViewOnly,
    }
  ) => {
    if (prevStyles !== nextStyles) return false;
    else if (prevOnChange !== nextOnChange) return false;
    else if (prevOnValidation !== nextOnValidation) return false;
    else if (prevSections !== nextSections) return false;
    else if (prevVehicles !== nextVehicles) return false;
    else if (prevRoutes !== nextRoutes) return false;
    else if (prevDrivers !== nextDrivers) return false;
    else if (prevDriverEdit !== nextDriverEdit) return false;
    else if (!isEqual(prevDisabledControls, nextDisabledControls)) return false;
    else if (!isEqual(prevHiddenControls, nextHiddenControls)) return false;
    else if (prevHideCheckboxInfoIcon !== nextHideCheckboxInfoIcon)
      return false;
    else if (prevViewOnly !== nextViewOnly) return false;
    else return true;
  }
);
